import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

// Google Analytics 4 Measurement ID
// 替换为你的实际 GA4 ID (格式: G-XXXXXXXXXX)
const GA_MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID || '';

// 扩展 Window 接口以支持 gtag
declare global {
    interface Window {
        dataLayer: any[];
        gtag: (...args: any[]) => void;
    }
}

export function Analytics() {
    const location = useLocation();

    useEffect(() => {
        // 如果没有配置 GA ID，则不加载
        if (!GA_MEASUREMENT_ID) {
            console.warn('Google Analytics ID not configured. Set VITE_GA_MEASUREMENT_ID in .env');
            return;
        }

        // 加载 Google Analytics 脚本
        const script = document.createElement('script');
        script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
        script.async = true;
        document.head.appendChild(script);

        // 初始化 dataLayer
        window.dataLayer = window.dataLayer || [];
        window.gtag = function gtag() {
            window.dataLayer.push(arguments);
        };
        window.gtag('js', new Date());
        window.gtag('config', GA_MEASUREMENT_ID, {
            page_path: location.pathname + location.search,
            send_page_view: true,
        });

        // 清理函数
        return () => {
            // 移除脚本（可选，通常不需要）
            // document.head.removeChild(script);
        };
    }, []); // 只在组件挂载时执行一次

    // 监听路由变化，发送页面浏览事件
    useEffect(() => {
        if (!GA_MEASUREMENT_ID || !window.gtag) return;

        window.gtag('event', 'page_view', {
            page_path: location.pathname + location.search,
            page_location: window.location.href,
            page_title: document.title,
        });
    }, [location]);

    // 此组件不渲染任何内容
    return null;
}

// 自定义事件跟踪函数
export const trackEvent = (
    eventName: string,
    eventParams?: {
        category?: string;
        label?: string;
        value?: number;
        [key: string]: any;
    }
) => {
    if (!GA_MEASUREMENT_ID || !window.gtag) {
        console.warn('Google Analytics not initialized');
        return;
    }

    window.gtag('event', eventName, eventParams);
};

// 常用事件跟踪辅助函数
export const analytics = {
    // 跟踪工具使用
    trackToolUsage: (toolName: string) => {
        trackEvent('tool_usage', {
            category: 'Tools',
            label: toolName,
        });
    },

    // 跟踪复制操作
    trackCopy: (toolName: string) => {
        trackEvent('copy', {
            category: 'Interaction',
            label: toolName,
        });
    },

    // 跟踪语言切换
    trackLanguageChange: (fromLang: string, toLang: string) => {
        trackEvent('language_change', {
            category: 'Settings',
            label: `${fromLang} → ${toLang}`,
        });
    },

    // 跟踪下载操作
    trackDownload: (fileName: string) => {
        trackEvent('download', {
            category: 'Interaction',
            label: fileName,
        });
    },

    // 跟踪错误
    trackError: (errorName: string, errorMessage: string) => {
        trackEvent('exception', {
            description: `${errorName}: ${errorMessage}`,
            fatal: false,
        });
    },
};
